'use client';

import { c as _c } from "react/compiler-runtime";
import { createLogger } from '@svenskaspelab/nova-core/log';
import '@svenskaspelab/nova-core/nextjs/analytics';
import { createMutationFn, wagerMutationKeys } from '../../../../lib/entry.js';
import { useMutation } from '../../../../tanstack/entry.js';
import { placeSelfServiceCouponWager } from '../../../actions/coupon/placeSelfServiceCouponWager.js';
const logger = createLogger('poolbetting.place-selfservice-coupon-wager');
const mutationFn = createMutationFn(placeSelfServiceCouponWager);
/**
 * React hook to add a coupon to the self-service cart.
 *
 * Self-service never charges the player, so unlike {@link import('./usePlaceCouponWager.js').usePlaceCouponWager}
 * this hook does not refresh the account balance and is independent of the user role and marketplace wallets.
 *
 * @param root0 - The parameters for adding a coupon to the self-service cart
 * @param root0.addItem - `addItem` from the self-service cart context, used to put the wager in the cart.
 * @param root0.regCloseTime - Optional registration close time of the draw, shown as description in the cart.
 * @param root0.brandLogo - Optional brand logo for the cart item, defaults to the lowercased product name.
 * @param root0.callbackOnDone - Optional callback invoked after the mutation settles, regardless of outcome.
 * @param root0.trackTransaction - Function to track ecommerce analytics events.
 * @param root0.trackEventFunction - Function to track general analytics events.
 * @returns Mutation result for adding a coupon to the self-service cart.
 */
export function usePlaceSelfServiceCouponWager(t0) {
  const $ = _c(14);
  const {
    addItem,
    regCloseTime,
    brandLogo,
    callbackOnDone,
    trackTransaction,
    trackEventFunction
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = wagerMutationKeys.placeSelfServiceCouponWager();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== addItem || $[2] !== brandLogo || $[3] !== regCloseTime || $[4] !== trackEventFunction || $[5] !== trackTransaction) {
    t2 = async props => mutationFn(props, {
      selfService: {
        addItem,
        regCloseTime,
        brandLogo
      },
      trackTransaction,
      trackEventFunction
    });
    $[1] = addItem;
    $[2] = brandLogo;
    $[3] = regCloseTime;
    $[4] = trackEventFunction;
    $[5] = trackTransaction;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  let t4;
  if ($[7] !== callbackOnDone) {
    t3 = data => {
      logger.info("Added coupons to self-service cart", {
        addedCouponsCount: data.addedCouponsCount
      });
      callbackOnDone?.();
    };
    t4 = error => {
      logger.error("Failed to add coupons to self-service cart", {
        error
      });
      callbackOnDone?.();
    };
    $[7] = callbackOnDone;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      mutationKey: t1,
      mutationFn: t2,
      onSuccess: t3,
      onError: t4
    };
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return useMutation(t5);
}